import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Tab,
  Tabs,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Button,
  Divider,
  Checkbox,
  useMediaQuery,
  useTheme,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  DirectionsCar,
  LocalGasStation,
  Speed,
  Settings,
  ColorLens,
  CheckCircle,
  Star,
  Build,
  ShoppingCart,
  Send as SendIcon,
  BrokenImage,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const API_DOMAIN = process.env.REACT_APP_DOMAIN || 'https://admin.aitomotivelab.com';
// Log once so we can verify which backend is used (e.g. in production)
if (typeof window !== 'undefined' && !window.__catalogTrimDialogDomainLogged) {
  console.log('[CatalogTrimDialog] API_DOMAIN', API_DOMAIN);
  window.__catalogTrimDialogDomainLogged = true;
}

const CHAT_ID_STORAGE_KEY = 'aitomotivelab_personalData_chat_id';

/** Return current chat id from localStorage if valid (numeric string). */
function getCurrentChatId() {
  const raw = localStorage.getItem(CHAT_ID_STORAGE_KEY);
  if (raw == null || String(raw).trim() === '') return null;
  const s = String(raw).trim();
  if (!/^\d+$/.test(s)) return null;
  return s;
}

const LOG_PREFIX = '[CatalogTrimDialog]';

/** Fetch catalog_token for a chat_id (fallback when card has no token, e.g. production webchat). */
async function fetchCatalogToken(chatId) {
  const url = `${API_DOMAIN}/car_stock/api/configurator/catalog/token/?chat_id=${encodeURIComponent(chatId)}`;
  console.log(LOG_PREFIX, 'fetchCatalogToken', { chatId, url });
  try {
    const res = await fetch(url);
    console.log(LOG_PREFIX, 'fetchCatalogToken response', { status: res.status, ok: res.ok });
    if (!res.ok) {
      let detail = null;
      try { detail = (await res.json()).detail; } catch (e) { console.warn(LOG_PREFIX, 'fetchCatalogToken parse error', e); }
      console.error(LOG_PREFIX, 'fetchCatalogToken failed', { status: res.status, detail });
      throw Object.assign(new Error(`${res.status}`), { serverDetail: detail });
    }
    const data = await res.json();
    return { chat_id: data.chat_id, catalog_token: data.catalog_token };
  } catch (err) {
    console.error(LOG_PREFIX, 'fetchCatalogToken error', err);
    throw err;
  }
}

// ─── Cache ─────────────────────────────────────────────────────────────────────
const trimCache = {};

async function fetchTrimDetail(providerCode, chatId, catalogToken) {
  const cacheKey = `${chatId}__${providerCode}`;
  if (trimCache[cacheKey]) return trimCache[cacheKey];
  if (!chatId || !catalogToken) {
    console.error(LOG_PREFIX, 'fetchTrimDetail Missing auth', { providerCode, hasChatId: !!chatId, hasToken: !!catalogToken });
    throw new Error('Missing auth');
  }
  const url = `${API_DOMAIN}/car_stock/api/configurator/catalog/trims/${providerCode}/?chat_id=${chatId}&catalog_token=${catalogToken}`;
  console.log(LOG_PREFIX, 'fetchTrimDetail', { providerCode, chatId, url: url.replace(catalogToken, '***') });
  try {
    const res = await fetch(url);
    console.log(LOG_PREFIX, 'fetchTrimDetail response', { status: res.status, ok: res.ok });
    if (!res.ok) {
      let detail = null;
      try { detail = (await res.json()).detail; } catch (e) { console.warn(LOG_PREFIX, 'fetchTrimDetail parse error', e); }
      console.error(LOG_PREFIX, 'fetchTrimDetail failed', { status: res.status, detail });
      throw Object.assign(new Error(`${res.status}`), { serverDetail: detail });
    }
    const data = await res.json();
    trimCache[cacheKey] = data;
    return data;
  } catch (err) {
    console.error(LOG_PREFIX, 'fetchTrimDetail error', err);
    throw err;
  }
}

// ─── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (v) =>
  v != null ? parseFloat(v).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : null;

const FUEL_COLORS = {
  benzina: '#ef4444', diesel: '#3b82f6',
  ibrida: '#10b981', ibrido: '#10b981',
  elettrica: '#8b5cf6', elettrico: '#8b5cf6',
  gpl: '#f59e0b', metano: '#06b6d4',
};
const fuelColor = (f) => {
  if (!f) return '#64748b';
  const lc = f.toLowerCase();
  return Object.entries(FUEL_COLORS).find(([k]) => lc.includes(k))?.[1] ?? '#64748b';
};

// ─── SpecPill ──────────────────────────────────────────────────────────────────
const SpecPill = ({ icon, label, value, accent }) => {
  if (!value && value !== 0) return null;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.75,
      bgcolor: accent ? `${accent}12` : '#f8fafc',
      border: `1px solid ${accent ? `${accent}30` : '#e2e8f0'}`,
      borderRadius: '12px', px: 1.5, py: 0.9, minWidth: 0,
    }}>
      <Box sx={{ color: accent || '#64748b', display: 'flex', flexShrink: 0, fontSize: 16 }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 500, lineHeight: 1 }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', mt: 0.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</Typography>
      </Box>
    </Box>
  );
};

// ─── PaintSwatch (interactive) ─────────────────────────────────────────────────
const PaintSwatch = ({ item, selected, onSelect }) => (
  <Box
    onClick={() => onSelect(item)}
    sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75,
      p: 1.25, borderRadius: '14px', cursor: 'pointer',
      border: `2px solid ${selected ? '#6366f1' : '#e2e8f0'}`,
      bgcolor: selected ? '#f0f0ff' : '#fafafa',
      transition: 'all 0.18s',
      minWidth: '80px', maxWidth: '100px',
      transform: selected ? 'scale(1.05)' : 'scale(1)',
      boxShadow: selected ? '0 0 0 3px rgba(99,102,241,0.2)' : 'none',
      '&:hover': { borderColor: '#6366f1', bgcolor: '#f5f3ff' },
    }}
  >
    <Box sx={{
      width: 40, height: 40, borderRadius: '50%',
      bgcolor: item.hex || '#e2e8f0',
      border: selected ? '3px solid #6366f1' : '3px solid #fff',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {selected && <CheckCircle sx={{ color: '#fff', fontSize: 18, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))' }} />}
    </Box>
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: selected ? '#4f46e5' : '#374151', textAlign: 'center', lineHeight: 1.2 }}>
      {item.description}
    </Typography>
    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: item.price > 0 ? '#6366f1' : '#10b981' }}>
      {item.price > 0 ? `+€${fmt(item.price)}` : 'incluso'}
    </Typography>
  </Box>
);

// ─── Main ──────────────────────────────────────────────────────────────────────
const CatalogTrimDialog = ({ open, onClose, car, onAskAssistant }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [tab, setTab] = useState(0);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imgError, setImgError] = useState(false);

  // ── Selection state ──────────────────────────────────────────────────────────
  const [selectedOptionals, setSelectedOptionals] = useState(new Set()); // Set<cod>
  const [selectedOptionalItems, setSelectedOptionalItems] = useState({}); // cod -> item
  const [selectedPaint, setSelectedPaint] = useState(null); // paint item or null

  const providerCode = car?.vehicleid?.replace(/^catalog_/, '') ?? '';
  const catalogToken = car?.catalog_token ?? null;
  const chatId = car?.catalog_chat_id ?? null;
  const artifactDetail = (car?.specs && car?.optional_equipment) ? car : null;

  // ── Load data ────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!open || !providerCode) return;
    console.log(LOG_PREFIX, 'load', { providerCode, hasChatId: !!chatId, hasCatalogToken: !!catalogToken, artifactDetail: !!artifactDetail });
    if (artifactDetail) { setDetail(artifactDetail); return; }

    let effectiveChatId = chatId != null ? String(chatId) : null;
    let effectiveToken = catalogToken ?? null;

    // Fallback: in production webchat the card may lack catalog_token/catalog_chat_id;
    // use current chat from localStorage and fetch token from API.
    if (!effectiveToken || !effectiveChatId) {
      const currentChatId = getCurrentChatId();
      console.log(LOG_PREFIX, 'load fallback', { currentChatIdFromStorage: currentChatId });
      if (currentChatId) {
        try {
          const { chat_id: cid, catalog_token: tok } = await fetchCatalogToken(currentChatId);
          effectiveChatId = effectiveChatId ?? String(cid);
          effectiveToken = effectiveToken ?? tok;
          console.log(LOG_PREFIX, 'load fallback got token', { effectiveChatId, hasToken: !!effectiveToken });
        } catch (fallbackErr) {
          console.error(LOG_PREFIX, 'load fallback error', fallbackErr);
          // keep effectiveChatId/effectiveToken as-is (may still be missing)
        }
      }
    }

    if (!effectiveToken || !effectiveChatId) {
      console.warn(LOG_PREFIX, 'load no auth', { effectiveChatId, hasToken: !!effectiveToken });
      setError('Token non disponibile. Fai una nuova ricerca per aggiornare i dati.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      setDetail(await fetchTrimDetail(providerCode, effectiveChatId, effectiveToken));
    } catch (err) {
      console.error(LOG_PREFIX, 'load fetchTrimDetail error', err, { message: err?.message, serverDetail: err?.serverDetail });
      setError(
        err?.serverDetail ||
        'Impossibile caricare i dettagli. Riprova più tardi.'
      );
    } finally {
      setLoading(false);
    }
  }, [open, providerCode, chatId, catalogToken, artifactDetail]);

  useEffect(() => {
    if (open) {
      setTab(0);
      setDetail(null);
      setSelectedOptionals(new Set());
      setSelectedOptionalItems({});
      setSelectedPaint(null);
      setImgError(false);
      load();
    }
  }, [open, load]);

  // ── Derived data ─────────────────────────────────────────────────────────────
  const specs = detail?.specs ?? {};
  const optEq = detail?.optional_equipment ?? {};
  const paintOpts = detail?.paint_options ?? {};
  const stdEq = detail?.standard_equipment ?? {};
  const allOptionals = useMemo(() => Object.values(optEq).flat(), [optEq]);
  const allPaints = useMemo(() => Object.values(paintOpts).flat(), [paintOpts]);
  const totalStd = useMemo(() => Object.values(stdEq).reduce((s, a) => s + a.length, 0), [stdEq]);

  // ── Selection handlers ───────────────────────────────────────────────────────
  const toggleOptional = (item) => {
    setSelectedOptionals(prev => {
      const next = new Set(prev);
      if (next.has(item.cod)) {
        next.delete(item.cod);
        setSelectedOptionalItems(p => { const n = { ...p }; delete n[item.cod]; return n; });
      } else {
        next.add(item.cod);
        setSelectedOptionalItems(p => ({ ...p, [item.cod]: item }));
      }
      return next;
    });
  };

  const togglePaint = (item) => {
    setSelectedPaint(prev => prev?.cod === item.cod ? null : item);
  };

  // ── Config total ─────────────────────────────────────────────────────────────
  const configTotal = useMemo(() => {
    const base = parseFloat(car?.saleprice || car?.description2 || 0);
    const paintCost = selectedPaint?.price ?? 0;
    const optsCost = Object.values(selectedOptionalItems).reduce((s, i) => s + (i.price || 0), 0);
    return base + paintCost + optsCost;
  }, [car, selectedPaint, selectedOptionalItems]);

  const selectionCount = selectedOptionals.size + (selectedPaint ? 1 : 0);

  // ── Send configuration ───────────────────────────────────────────────────────
  const handleSendConfig = () => {
    const lines = [
      `Vorrei configurare il **${car?.title ?? 'veicolo'}** (codice allestimento: ${providerCode}).`,
    ];
    if (selectedPaint) {
      lines.push(`\nColore scelto: ${selectedPaint.description} (cod: ${selectedPaint.cod})${selectedPaint.price > 0 ? ` – +€${fmt(selectedPaint.price)}` : ' – incluso'}`);
    }
    if (selectedOptionals.size > 0) {
      lines.push('\nOptional selezionati:');
      Object.values(selectedOptionalItems).forEach(item => {
        lines.push(`- ${item.description} (cod: ${item.cod})${item.price > 0 ? ` – +€${fmt(item.price)}` : ' – incluso'}`);
      });
    }
    if (configTotal > 0) {
      lines.push(`\nPrezzo stimato configurazione: €${fmt(configTotal)}`);
    }
    onAskAssistant?.(lines.join('\n'));
    onClose();
  };

  const handleAskOnly = () => {
    onAskAssistant?.(`Vorrei configurare il ${car?.title ?? 'veicolo'}. Codice allestimento: ${providerCode}`);
    onClose();
  };

  // ── Hero image ───────────────────────────────────────────────────────────────
  const heroImg = car?.image || car?.urlmainimage || null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '20px',
          maxHeight: isMobile ? '100dvh' : '92vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 60px rgba(0,0,0,0.18)',
        },
      }}
    >
      {/* ── HEADER ── */}
      <Box sx={{ flexShrink: 0, background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', position: 'relative' }}>
        {/* Hero image */}
        {heroImg && !imgError ? (
          <Box sx={{ position: 'relative', width: '100%', height: isMobile ? 160 : 200, overflow: 'hidden' }}>
            <Box
              component="img"
              src={heroImg}
              alt={car?.title}
              onError={() => setImgError(true)}
              sx={{
                width: '100%', height: '100%', objectFit: 'cover',
                filter: 'brightness(0.75)',
              }}
            />
            {/* Gradient overlay */}
            <Box sx={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(to bottom, rgba(99,102,241,0.4) 0%, rgba(79,70,229,0.85) 100%)',
            }} />
            {/* Close button */}
            <IconButton onClick={onClose} size="small" sx={{
              position: 'absolute', top: 10, right: 10,
              color: '#fff', bgcolor: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
            }}>
              <CloseIcon fontSize="small" />
            </IconButton>
            {/* Info overlay */}
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, px: 2.5, pb: 2, pt: 4 }}>
              <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.8)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.25 }}>
                Catalogo · Ordinabile
              </Typography>
              <Typography sx={{ fontSize: isMobile ? '1rem' : '1.15rem', fontWeight: 800, color: '#fff', lineHeight: 1.2, mb: 0.4 }}>
                {car?.title}
              </Typography>
              {car?.saleprice > 0 || car?.description2 > 0 ? (
                <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)', fontWeight: 700 }}>
                  da €{fmt(car?.saleprice || car?.description2)}
                </Typography>
              ) : null}
            </Box>
          </Box>
        ) : (
          /* Fallback header (no image) */
          <Box sx={{ px: 2.5, pt: 2.5, pb: 2, position: 'relative' }}>
            <IconButton onClick={onClose} size="small" sx={{
              position: 'absolute', top: 10, right: 10,
              color: 'rgba(255,255,255,0.85)', bgcolor: 'rgba(255,255,255,0.15)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
            }}>
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box sx={{ display: 'flex', gap: 1.5, pr: 4, alignItems: 'center' }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {imgError ? <BrokenImage sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 24 }} /> : <DirectionsCar sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 24 }} />}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.25 }}>Catalogo · Ordinabile</Typography>
                <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>{car?.title}</Typography>
                {(car?.saleprice > 0 || car?.description2 > 0) && (
                  <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)', fontWeight: 700, mt: 0.3 }}>
                    da €{fmt(car?.saleprice || car?.description2)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Quick spec chips */}
        <Box sx={{ display: 'flex', gap: 0.75, px: 2.5, pb: 1.5, flexWrap: 'wrap' }}>
          {car?.fuel_type && <Chip label={car.fuel_type} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 600, fontSize: '0.72rem', height: 22 }} />}
          {car?.power_hp && <Chip label={`${car.power_hp} CV`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 600, fontSize: '0.72rem', height: 22 }} />}
          {car?.transmission && <Chip label={car.transmission} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 600, fontSize: '0.72rem', height: 22 }} />}
        </Box>
      </Box>

      {/* ── TABS ── */}
      <Box sx={{ flexShrink: 0, bgcolor: '#fff', borderBottom: '1px solid #e2e8f0' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: isMobile ? 40 : 44,
            '& .MuiTab-root': {
              minHeight: isMobile ? 40 : 44,
              fontSize: isMobile ? '0.75rem' : '0.82rem',
              fontWeight: 600,
              textTransform: 'none',
              px: isMobile ? 1.5 : 2,
              minWidth: 'auto',
            },
            '& .Mui-selected': { color: '#6366f1' },
            '& .MuiTabs-indicator': { bgcolor: '#6366f1', height: 3 },
          }}
        >
          <Tab label={isMobile ? 'Specs' : 'Specifiche'}
            icon={<Speed sx={{ fontSize: isMobile ? 14 : 15 }} />} iconPosition="start" />
          <Tab
            label={isMobile
              ? `Optional${allOptionals.length ? ` (${allOptionals.length})` : ''}`
              : `Optional${allOptionals.length ? ` (${allOptionals.length})` : ''}`}
            icon={
              <Badge badgeContent={selectedOptionals.size || null} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 14, minWidth: 14 } }}>
                <Build sx={{ fontSize: isMobile ? 14 : 15 }} />
              </Badge>
            }
            iconPosition="start"
          />
          <Tab label={isMobile
            ? `Colori${allPaints.length ? ` (${allPaints.length})` : ''}`
            : `Colori${allPaints.length ? ` (${allPaints.length})` : ''}`}
            icon={
              <Badge variant={selectedPaint ? 'dot' : 'standard'} color="primary">
                <ColorLens sx={{ fontSize: isMobile ? 14 : 15 }} />
              </Badge>
            }
            iconPosition="start"
          />
          <Tab label={isMobile ? `Dotaz. (${totalStd})` : `Dotazione (${totalStd})`}
            icon={<CheckCircle sx={{ fontSize: isMobile ? 14 : 15 }} />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* ── CONTENT ── */}
      <DialogContent sx={{ p: 0, overflow: 'auto', flex: 1 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#6366f1' }} />
          </Box>
        )}
        {error && !loading && (
          <Box sx={{ p: 3 }}>
            <Alert severity="warning" action={
              <Button size="small" onClick={handleAskOnly} sx={{ fontWeight: 700 }}>Chiedi all'assistente</Button>
            }>{error}</Alert>
          </Box>
        )}

        {!loading && !error && detail && (
          <AnimatePresence mode="wait">
            <motion.div key={tab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.15 }}>

              {/* ── TAB 0: Specifiche ── */}
              {tab === 0 && (
                <Box sx={{ p: isMobile ? 2 : 3 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '140px' : '155px'}, 1fr))`, gap: 1.25 }}>
                    <SpecPill icon={<LocalGasStation sx={{ fontSize: 17 }} />} label="Carburante" value={specs.fuel_type} accent={fuelColor(specs.fuel_type)} />
                    <SpecPill icon={<Speed sx={{ fontSize: 17 }} />} label="Potenza" value={specs.power_hp ? `${specs.power_hp} CV${specs.power_kw ? ` / ${specs.power_kw} kW` : ''}` : null} accent="#6366f1" />
                    {specs.power_hp_electric && <SpecPill icon={<Speed sx={{ fontSize: 17 }} />} label="Mot. Elettrico" value={`${specs.power_hp_electric} CV`} accent="#8b5cf6" />}
                    <SpecPill icon={<Settings sx={{ fontSize: 17 }} />} label="Cambio" value={specs.transmission} />
                    <SpecPill icon={<DirectionsCar sx={{ fontSize: 17 }} />} label="Trazione" value={specs.traction} />
                    {specs.torque && <SpecPill icon={<Speed sx={{ fontSize: 17 }} />} label="Coppia" value={`${specs.torque} Nm`} />}
                    {specs.top_speed_kmh && <SpecPill icon={<Speed sx={{ fontSize: 17 }} />} label="Vel. max" value={`${specs.top_speed_kmh} km/h`} />}
                    {specs.acceleration_0_100 && <SpecPill icon={<Speed sx={{ fontSize: 17 }} />} label="0–100 km/h" value={`${specs.acceleration_0_100} s`} />}
                    {specs.num_doors && <SpecPill icon={<DirectionsCar sx={{ fontSize: 17 }} />} label="Porte" value={specs.num_doors} />}
                    {specs.num_seats && <SpecPill icon={<DirectionsCar sx={{ fontSize: 17 }} />} label="Posti" value={specs.num_seats} />}
                    {specs.trunk_min_l && <SpecPill icon={<DirectionsCar sx={{ fontSize: 17 }} />} label="Bagagliaio" value={specs.trunk_max_l ? `${specs.trunk_min_l}–${specs.trunk_max_l} L` : `${specs.trunk_min_l} L`} />}
                    {specs.mass_kg && <SpecPill icon={<DirectionsCar sx={{ fontSize: 17 }} />} label="Peso" value={`${specs.mass_kg} kg`} />}
                    {specs.length_mm && <SpecPill icon={<DirectionsCar sx={{ fontSize: 17 }} />} label="Lunghezza" value={`${(specs.length_mm / 1000).toFixed(2)} m`} />}
                  </Box>
                  {specs.engine && (
                    <Box sx={{ mt: 2, p: 1.75, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, mb: 0.4 }}>Motore</Typography>
                      <Typography sx={{ fontSize: '0.88rem', color: '#1e293b', fontWeight: 600 }}>{specs.engine}</Typography>
                    </Box>
                  )}
                  {specs.young_driver_eligible && (
                    <Chip icon={<Star sx={{ fontSize: 14 }} />} label="Idoneo neopatentati" size="small"
                      sx={{ mt: 1.5, bgcolor: '#fef9c3', color: '#854d0e', fontWeight: 600 }} />
                  )}
                </Box>
              )}

              {/* ── TAB 1: Optional (interactive) ── */}
              {tab === 1 && (
                <Box sx={{ p: isMobile ? 1.5 : 3 }}>
                  {selectedOptionals.size > 0 && (
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f0f0ff', borderRadius: '12px', border: '1px solid #c7d2fe', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <Typography sx={{ fontSize: '0.82rem', color: '#4338ca', fontWeight: 700 }}>
                        {selectedOptionals.size} optional selezionat{selectedOptionals.size === 1 ? 'o' : 'i'}
                      </Typography>
                      <Button size="small" onClick={() => { setSelectedOptionals(new Set()); setSelectedOptionalItems({}); }}
                        sx={{ fontSize: '0.75rem', color: '#6366f1', textTransform: 'none', fontWeight: 600 }}>
                        Deseleziona tutti
                      </Button>
                    </Box>
                  )}
                  {Object.keys(optEq).length === 0 ? (
                    <Typography sx={{ color: '#64748b', textAlign: 'center', py: 4 }}>Nessun optional disponibile</Typography>
                  ) : (
                    Object.entries(optEq).map(([group, items]) => (
                      <Accordion key={group} defaultExpanded disableGutters elevation={0}
                        sx={{ mb: 1, border: '1px solid #e2e8f0', borderRadius: '12px !important', overflow: 'hidden', '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}
                          sx={{ px: 1.75, py: 0.25, bgcolor: '#f8fafc', '& .MuiAccordionSummary-content': { my: 0.75 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>{group}</Typography>
                            <Chip
                              label={`${items.filter(i => selectedOptionals.has(i.cod)).length}/${items.length}`}
                              size="small"
                              sx={{
                                height: 20, fontSize: '0.7rem', fontWeight: 700,
                                bgcolor: items.some(i => selectedOptionals.has(i.cod)) ? '#e0e7ff' : '#f1f5f9',
                                color: items.some(i => selectedOptionals.has(i.cod)) ? '#4338ca' : '#64748b',
                              }}
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0.5 }}>
                          {items.map((item, i) => {
                            const isSelected = selectedOptionals.has(item.cod);
                            return (
                              <Box
                                key={i}
                                onClick={() => toggleOptional(item)}
                                sx={{
                                  display: 'flex', alignItems: 'center', gap: 1,
                                  py: 0.65, px: 1, borderRadius: '10px', cursor: 'pointer',
                                  bgcolor: isSelected ? '#f0f0ff' : (i % 2 === 0 ? '#f8fafc' : 'transparent'),
                                  border: `1px solid ${isSelected ? '#c7d2fe' : 'transparent'}`,
                                  mb: 0.5, transition: 'all 0.15s',
                                  '&:hover': { bgcolor: '#f0f0ff', borderColor: '#c7d2fe' },
                                }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={isSelected}
                                  onChange={() => toggleOptional(item)}
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{ p: 0, color: '#c7d2fe', '&.Mui-checked': { color: '#6366f1' }, flexShrink: 0 }}
                                />
                                <Typography sx={{ fontSize: '0.82rem', color: '#374151', flex: 1, lineHeight: 1.3, fontWeight: isSelected ? 600 : 400 }}>
                                  {item.description}
                                </Typography>
                                <Chip
                                  label={item.price > 0 ? `+€${fmt(item.price)}` : 'incluso'}
                                  size="small"
                                  sx={{
                                    flexShrink: 0, height: 22, fontSize: '0.72rem', fontWeight: 700,
                                    bgcolor: item.price > 0 ? '#fef3c7' : '#d1fae5',
                                    color: item.price > 0 ? '#d97706' : '#065f46',
                                  }}
                                />
                              </Box>
                            );
                          })}
                        </AccordionDetails>
                      </Accordion>
                    ))
                  )}
                </Box>
              )}

              {/* ── TAB 2: Colori (interactive) ── */}
              {tab === 2 && (
                <Box sx={{ p: isMobile ? 1.5 : 3 }}>
                  {selectedPaint && (
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f0f0ff', borderRadius: '12px', border: '1px solid #c7d2fe', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: selectedPaint.hex || '#e2e8f0', border: '2px solid #6366f1', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.82rem', color: '#4338ca', fontWeight: 700, flex: 1 }}>
                        {selectedPaint.description}
                        {selectedPaint.price > 0 ? ` – +€${fmt(selectedPaint.price)}` : ' – incluso'}
                      </Typography>
                      <Button size="small" onClick={() => setSelectedPaint(null)} sx={{ fontSize: '0.75rem', color: '#6366f1', textTransform: 'none', fontWeight: 600 }}>
                        Cambia
                      </Button>
                    </Box>
                  )}
                  {Object.keys(paintOpts).length === 0 ? (
                    <Typography sx={{ color: '#64748b', textAlign: 'center', py: 4 }}>Nessuna opzione colore disponibile</Typography>
                  ) : (
                    Object.entries(paintOpts).map(([ptype, items]) => (
                      <Box key={ptype} sx={{ mb: 2.5 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#374151', mb: 1.25 }}>{ptype}</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                          {items.map((item, i) => (
                            <PaintSwatch key={i} item={item} selected={selectedPaint?.cod === item.cod} onSelect={togglePaint} />
                          ))}
                        </Box>
                        <Divider sx={{ mt: 2 }} />
                      </Box>
                    ))
                  )}
                </Box>
              )}

              {/* ── TAB 3: Dotazione standard ── */}
              {tab === 3 && (
                <Box sx={{ p: isMobile ? 1.5 : 3 }}>
                  {Object.keys(stdEq).length === 0 ? (
                    <Typography sx={{ color: '#64748b', textAlign: 'center', py: 4 }}>Nessuna dotazione disponibile</Typography>
                  ) : (
                    Object.entries(stdEq).map(([group, items]) => (
                      <Accordion key={group} defaultExpanded={false} disableGutters elevation={0}
                        sx={{ mb: 1, border: '1px solid #e2e8f0', borderRadius: '12px !important', overflow: 'hidden', '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}
                          sx={{ px: 1.75, py: 0.25, bgcolor: '#f8fafc', '& .MuiAccordionSummary-content': { my: 0.75 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>{group}</Typography>
                            <Chip label={items.length} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#dcfce7', color: '#166534', fontWeight: 700 }} />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0.5 }}>
                          {items.map((item, i) => (
                            <Box key={i} sx={{ py: 0.55, px: 1, borderRadius: '8px', bgcolor: i % 2 === 0 ? '#f8fafc' : 'transparent', mb: 0.25 }}>
                              <Typography sx={{ fontSize: '0.82rem', color: '#374151', fontWeight: 500 }}>{item.name}</Typography>
                              {item.description && item.description !== item.name && (
                                <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.1 }}>{item.description}</Typography>
                              )}
                            </Box>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    ))
                  )}
                </Box>
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </DialogContent>

      {/* ── FOOTER ── */}
      <Box sx={{
        flexShrink: 0,
        borderTop: '1px solid #e2e8f0',
        bgcolor: '#fff',
        p: isMobile ? 1.5 : 2.5,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 1.25,
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
      }}>
        {/* Price summary */}
        {detail && configTotal > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500 }}>
              {selectionCount > 0 ? 'Prezzo stimato' : 'Prezzo base'}
            </Typography>
            <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
              €{fmt(configTotal)}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, flex: 1, justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              borderRadius: '12px', textTransform: 'none', fontWeight: 600,
              borderColor: '#e2e8f0', color: '#64748b', flexShrink: 0,
              ...(isMobile ? {} : {}),
            }}
          >
            Chiudi
          </Button>

          {selectionCount > 0 ? (
            <Button
              variant="contained"
              onClick={handleSendConfig}
              startIcon={<SendIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderRadius: '12px', textTransform: 'none', fontWeight: 700,
                bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' },
                boxShadow: '0 4px 12px rgba(99,102,241,0.35)',
                flex: isMobile ? 1 : 'none',
              }}
            >
              Invia configurazione
              <Chip
                label={selectionCount}
                size="small"
                sx={{ ml: 1, height: 20, fontSize: '0.72rem', bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 700 }}
              />
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleAskOnly}
              sx={{
                borderRadius: '12px', textTransform: 'none', fontWeight: 700,
                bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' },
                boxShadow: '0 4px 12px rgba(99,102,241,0.35)',
                flex: isMobile ? 1 : 'none',
              }}
            >
              Configura con l'assistente
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default CatalogTrimDialog;
